import { makeStyles } from '@material-ui/core'
import { chatDrawerActions, chatDrawerSelectors } from 'ducks/chatDrawer'
import { BetaChip } from 'elements/chip/BetaChip'
import Tooltip from 'elements/tooltip/Tooltip'
import { ComponentVersionsInherit, IconButtonProps, OsTooltipVersion } from 'opensolar-ui'
import { useTranslate } from 'react-admin'
import { useDispatch, useSelector } from 'react-redux'
import type { OpenSolarThemeType } from 'Themes'
import AdaIcon from './AdaIcon'

const useStyles = makeStyles((theme: OpenSolarThemeType) => ({
  icon: {
    height: 24,
    width: 24,
    float: 'right',
    padding: 0,
    margin: 0,
    marginLeft: 0,
  },
  iconButtonRoot: {
    minWidth: 36,
    padding: 0,
    height: 36,
    '&:focus': {
      outline: 'none',
    },
    '&:focus-visible': {
      outline: 'none',
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&.ChatDrawerBottomButton': {
      border: 'none',
      '&:hover': {
        backgroundColor: 'transparent !important',
        '@media (hover: hover)': {
          backgroundColor: 'transparent !important',
        },
      },
      '&:active': {
        backgroundColor: 'transparent !important',
      },
    },
  },
  iconButtonPrimary: {
    color: theme.helpColor,
  },
  buttonContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: 65,
    height: 28,
    padding: '4px 5px 4px 11px',
    borderRadius: 4,
    border: '1px solid #ECECED',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#D6E0F7',
    },
  },
  buttonContainerActive: {
    borderColor: '#D6E0F7',
    background:
      'linear-gradient(270deg, rgba(23, 81, 208, 0.15) -27.75%, rgba(53, 124, 230, 0.15) 10%, rgba(83, 167, 252, 0.15) 47.75%, rgba(83, 167, 252, 0.02) 161%)',
  },
  buttonContainerWithBadge: {
    width: 80,
    padding: '4px 8px 4px 11px',
  },
  adaText: {
    fontSize: 16,
    fontWeight: 500,
    color: '#4272DD',
    userSelect: 'none',
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  unreadBadge: {
    minWidth: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: '#4272DD',
    color: 'white',
    fontSize: 10,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 'auto',
  },
}))

const ChatDrawerButton = ({ iconColor = 'inherit' }: { iconColor?: IconButtonProps['color'] }) => {
  const translate = useTranslate()
  const dispatch = useDispatch()
  const unreadCount = useSelector(chatDrawerSelectors.getUnreadCount)
  const hasUnreadMessages = useSelector(chatDrawerSelectors.getHasUnreadMessages)
  const chatDrawerOpen = useSelector(chatDrawerSelectors.getChatDrawerOpen)
  const classes = useStyles()

  const toggleChatDrawer = () => {
    dispatch(chatDrawerActions.setChatDrawerOpen(!chatDrawerOpen))
  }

  const buttonClasses = [
    classes.buttonContainer,
    chatDrawerOpen && classes.buttonContainerActive,
    hasUnreadMessages && classes.buttonContainerWithBadge,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <ComponentVersionsInherit versions={{ tooltip: OsTooltipVersion.V2 }}>
      <Tooltip title={translate('Chat with Ada')} enterDelay={300}>
        <div className={buttonClasses} onClick={toggleChatDrawer}>
          <div className={classes.iconWrapper}>
            <AdaIcon size={14} showHoverEffects={false} />
          </div>
          <span className={classes.adaText}>Ada</span>
          {hasUnreadMessages && <div className={classes.unreadBadge}>{unreadCount > 9 ? '9+' : unreadCount}</div>}
          <BetaChip badge="top-right" size="xXSmall" tooltip={false} />
        </div>
      </Tooltip>
    </ComponentVersionsInherit>
  )
}

export default ChatDrawerButton
